// (!) Do not import from tsconfig paths; e.g. no $s, $t, $... The reason is that this file
// Is imported inside tests. The current ts-node config does not support ts-config.json paths.
// Package https://www.npmjs.com/package/tsconfig-paths could enable this.
import {nextTick} from '@bitstillery/mithril'
import {PlausibleEvent} from '@bitstillery/common/types/utils'
import m, {type RouteParamValue, type RouteParams} from '@bitstillery/mithril'
import dayjs from 'dayjs/esm/index'
import classnames from 'classnames'
import isBetween from 'dayjs/esm/plugin/isBetween/index'
import localizedFormat from 'dayjs/esm/plugin/localizedFormat/index'
import {unchecked} from '@bitstillery/common/lib/unchecked'

export * as path_to_regexp from 'path-to-regexp'

/**
 * Waits for multiple ticks in series
 * @param count - Number of ticks to wait for (default: 2)
 */
export async function next_ticks(count: number = 2): Promise<void> {
    for (let i = 0; i < count; i++) {
        await nextTick()
    }
}
// Accepts multiple arguments and types ('', [], {})
// See https://github.com/JedWatson/classnames
export const classes = classnames

dayjs.extend(isBetween)
dayjs.extend(localizedFormat)
export {dayjs}

export function add_unique_to_array<T>(array: T[], value: T): T[] {
    if (!array.includes(value)) {
        array.push(value)
    }
    return array
}

/**
 * A field that something fills before anything reads it — `bootstrap` loading a context's entity, or
 * `oncreate` capturing a DOM node — typed as the filled value so readers use it directly:
 * `sales_order: pending<X>()` in a data literal, `$container: HTMLElement = pending()` on a class, and
 * `context.data.sales_order = pending()` where bootstrap resets it before loading again. The
 * placeholder is null, which is what a read before the fill would find, and nothing reads it then.
 */
export function pending<T>(): T {
    return unchecked<T>(null)
}

/**
 * `pending` for an object that is filled in place (`Object.assign`) instead of replaced, such as a
 * collection's filters: the placeholder is an empty object, so code that iterates it before the fill
 * finds no keys, and nothing reads a key by name until then.
 */
export function pending_object<T extends object>(): T {
    return unchecked<T>({})
}

/**
 * A value the caller knows is set by the time this runs — loaded before the view renders, say — typed
 * as such. It throws exactly where dereferencing null would have, only with a message that names what
 * was missing, so use it right before the value is used and never as a guard: a value that can
 * legitimately be absent needs a real branch instead.
 */
export function present<T>(value: T | null | undefined, what: string): T {
    if (value === null || value === undefined) {
        throw new Error(`${what} is not loaded`)
    }
    return value
}

/**
 * The member of the string enum `members` that `value` is, or undefined when it's none of them. For a
 * string the types can't vouch for — a field the API schema declares as a plain `str`, a URL parameter —
 * so it compares against the enum's members rather than against string literals.
 */
export function enum_member<T extends string>(members: Record<string, T>, value: string | null | undefined): T | undefined {
    return Object.values(members).find((member) => member === value)
}

/**
 * A route param that is only ever meant as text: an id, a reference, a search term, a redirect path.
 * The querystring parser also turns a bare `true`/`false` into a boolean and a bracketed key into an
 * array or object; for these params that's never what the link meant, so they're read back as text.
 */
export function route_param_text(value: RouteParamValue): string
export function route_param_text(value: RouteParamValue | undefined): string | undefined
export function route_param_text(value: RouteParamValue | undefined): string | undefined {
    if (value === undefined || typeof value === 'string') {
        return value
    }
    if (typeof value === 'boolean') {
        return String(value)
    }
    // The comma join is what String() makes of an array.
    if (Array.isArray(value)) {
        return value.map((item) => route_param_text(item)).join(',')
    }
    return JSON.stringify(value)
}

/** Every route param as text (see route_param_text), as a querystring carries them. */
export function route_params_text(params: RouteParams): Record<string, string> {
    return Object.fromEntries(Object.entries(params).map(([key, value]) => [key, route_param_text(value)]))
}

/**
 * A `:key` of the matched route's template. The router fills those from the path, decoded, on every
 * match — so on a component that only the routes with `:key` render, it's always a string.
 */
export function route_path_param(key: string): string {
    const value = m.route.param(key)
    if (typeof value !== 'string') {
        throw new Error(`route param :${key} is not in the matched route's path`)
    }
    return value
}

/** A deep copy through JSON: functions, dates and undefined don't survive it, so copy plain data only. */
export function copy_object<T>(obj: T): T {
    return unchecked<T>(JSON.parse(JSON.stringify(obj)))
}

export async function blob_to_base64(blob: Blob): Promise<string> {
    return new Promise((resolve, reject) => {
        const reader = new FileReader()
        reader.onloadend = () => {
            // The "data:image/...;base64," prefix is part of the data URL scheme,
            // And must be omitted from the raw base64.
            const base64_data = typeof reader.result === 'string' ? reader.result : ''
            // Chrome reads an empty file as a bare `data:`, with no comma and so no payload after it.
            let raw_base64_data = base64_data.split(',')[1] ?? ''

            // Make sure the base64 is padded correctly, before sending to the backend.
            const padding = '='.repeat((4 - (raw_base64_data.length % 4)) % 4)
            raw_base64_data += padding
            resolve(raw_base64_data)
        }
        reader.addEventListener('error', reject)
        reader.readAsDataURL(blob)
    })
}

/**
 * Converts a base64 encoded string to a Uint8Array of bytes.
 * This function is useful for decoding a base64 string to its original binary form.
 * @param base64 The base64 encoded string to convert.
 * @returns A Uint8Array representing the decoded bytes of the base64 string.
 */
export function base64_to_bytes(base64: string): Uint8Array<ArrayBuffer> {
    const binary_string = atob(base64)
    const len = binary_string.length
    const bytes = new Uint8Array(len)

    for (let i = 0; i < len; i++) {
        bytes[i] = binary_string.charCodeAt(i)
    }

    return bytes
}

/**
 * Converts "2024-09-25T00:00:00Z" to "2024-09-25"
 * @param dateTimeString
 * @returns
 */
export function datetime_to_date(datetime: unknown) {
    if (typeof datetime !== 'string') {
        return null
    }

    if (!datetime.includes('T')) {
        throw new Error('invalid datetime string')
    }
    return dayjs(datetime).format('YYYY-MM-DD')
}

export function data_from_blueprint(data: Record<string, unknown>, blueprint: Record<string, unknown>) {
    for (const key of Object.keys(data)) {
        if (Object.hasOwn(blueprint, key)) {
            const value = data[key]
            if (is_nested_object(value)) {
                const nested_blueprint = blueprint[key]
                if (is_nested_object(nested_blueprint)) {
                    data_from_blueprint(value, nested_blueprint)
                }
            } else {
                data[key] = blueprint[key]
            }
        }
    }
    return data
}

/**
 * Dereference a prop followed by a path of optional selectors.
 * This function is useful if you don't know if the prop you have
 * is a window.prop (function) or just a plain value.
 *
 * Example:
 * const purchaseOrder = { reference: () => 'P123' };
 * console.log(deref(purchaseOrder, (d) => d.reference)); // 'P123'
 *
 * // The following inputs give the same answer:
 * // const purchaseOrder = { reference: () => 'P123' };
 * // const purchaseOrder = { reference: 'P123' };
 * // const purchaseOrder = () => ({ reference: 'P123' });
 */
export function deref<T>(prop: T | (() => T)): T
export function deref(prop: unknown, ...selectors: ((input: unknown) => unknown)[]): unknown
export function deref(prop: unknown, ...selectors: ((input: unknown) => unknown)[]): unknown {
    if (selectors.length > 0) {
        const [fn, ...rest] = selectors
        return deref(present(fn, 'selector')(deref(prop)), ...rest)
    } else if (typeof prop === 'function') {
        return Reflect.apply(prop, undefined, [])
    }
    return prop
}

/**
 * Downloads a file using a base64 encoded string.
 * @param base64 The base64 encoded string representing the file content.
 * @param filename The name of the file to be downloaded.
 */
export function download_base64_file(base64: string, filename: string) {
    download_blob(new Blob([base64_to_bytes(base64)], {type: 'application/octet-stream'}), filename)
}

/** Hands a blob to the browser as a file download. */
export function download_blob(blob: Blob, filename: string) {
    const a = document.createElement('a')
    document.body.append(a) // Necessary for Firefox(?)
    const url = window.URL.createObjectURL(blob)
    a.href = url
    a.download = filename
    a.click()

    window.URL.revokeObjectURL(url)
    document.body.removeChild(a)
}

/**
 * Converts a nested object where values are function calls into a regular object
 * by evaluating all function calls recursively
 * @param obj The object to convert
 * @returns A new object with all function calls evaluated
 */
export function evaluate_props(obj: unknown, memo: WeakMap<object, unknown> = new WeakMap()): unknown {
    if (typeof obj !== 'object' || obj === null) {
        return obj
    }

    if (memo.has(obj)) {
        return memo.get(obj)
    }

    if (Array.isArray(obj)) {
        const out: unknown[] = []
        memo.set(obj, out)
        for (let i = 0; i < obj.length; i++) {
            out[i] = evaluate_props(obj[i], memo)
        }
        return out
    }

    const result: Record<string, unknown> = {}
    memo.set(obj, result)
    for (const key of Object.keys(obj)) {
        const value: unknown = Reflect.get(obj, key)
        if (typeof value === 'function') {
            const evaluated_value: unknown = Reflect.apply(value, undefined, [])
            result[key] = evaluate_props(evaluated_value, memo)
        } else if (typeof value === 'object' && value !== null) {
            result[key] = evaluate_props(value, memo)
        } else {
            result[key] = value
        }
    }
    return result
}

export async function focus_field(tabindex: number) {
    await next_ticks(2)
    const element = document.querySelector<HTMLElement>(`[tabindex="${tabindex}"]`)
    if (element) {
        requestAnimationFrame(() => {
            element.focus()
        })
    }
}

/**
 * Generates a random transaction id.
 *
 * See https://blog.sentry.io/2019/04/04/trace-errors-through-stack-using-unique-identifiers-in-sentry
 */
export function generate_transaction_id(): string {
    return generate_unique_id()
}

/**
 * Generate unique ID.
 * See https://gist.github.com/gordonbrander/2230317.
 * @returns string
 */
export function generate_unique_id(): string {
    return Math.random().toString(36).slice(2, 9)
}

/**
 * Fast and simple insecure string hash for JavaScript
 * See https://gist.github.com/jlevy/c246006675becc446360a798e2b2d781
 */
export function hash(str: string) {
    let hash_value = 0
    for (let i = 0; i < str.length; i++) {
        const char = str.codePointAt(i)
        hash_value = (hash_value << 5) - hash_value + Number(char)
        hash_value &= hash_value // Convert to 32bit integer
    }
    return (hash_value >>> 0).toString(36)
}

export function is_nested_object(value: unknown): value is Record<string, unknown> {
    return value !== null && typeof value === 'object' && !Array.isArray(value)
}

/** Narrows `value` to a member of `values`, such as `Object.values(SomeEnum)` or a literal-union list. */
export function is_one_of<T>(values: readonly T[], value: unknown): value is T {
    return values.some((candidate) => candidate === value)
}

/** `value[key]` for a value of any type; undefined for null, undefined and other primitives. */
export function property_of(value: unknown, key: PropertyKey): unknown {
    return (typeof value === 'object' && value !== null) || typeof value === 'function' ? Reflect.get(value, key) : undefined
}

export function is_object(v: unknown) {
    return v && typeof v === 'object' && !Array.isArray(v)
}

export function is_promise(value: unknown): value is PromiseLike<unknown> {
    return typeof property_of(value, 'then') === 'function'
}

/**
 * Returns a function, that, as long as it continues to be invoked, will not be
 * triggered. The function will be called after it stops being called for N
 * milliseconds.
 */
export function debounce<A extends unknown[], R, This = unknown>(wait: number, func: (this: This, ...args: A) => R) {
    type Timeout = ReturnType<typeof setTimeout>
    interface Context {
        timeout: Timeout | undefined
        run: (() => void) | undefined
    }
    const ctx: Context = {timeout: undefined, run: undefined}
    const callback = function callback(this: This, ...args: A) {
        return new Promise<R>((resolve) => {
            const later = () => {
                ctx.timeout = undefined
                ctx.run = undefined
                resolve(func.apply(this, args))
            }
            if (ctx.timeout) {
                clearTimeout(ctx.timeout)
            }

            ctx.run = later
            ctx.timeout = setTimeout(ctx.run, wait)
        })
    }
    /** Run a waiting call now instead of when its timer expires; a no-op when nothing waits. */
    callback.flush = () => {
        if (!ctx.timeout) {
            return
        }
        clearTimeout(ctx.timeout)
        ctx.run?.()
    }
    return callback
}

export function delay<T>(msec: number, value: T): Promise<T> {
    return new Promise<T>((done) => window.setTimeout(() => done(value), msec))
}

/**
 * Get the descendant of an object by string. E.g.:
 * get_descendant_prop({ a: { b: { c: { d: 3 } } } }, "a.b.c.d")
 * returns 3
 * @param obj An object
 * @param desc A string representing the path to the descendant
 */
export function get_descendant_prop(obj: unknown, desc: string): unknown {
    const arr = desc.split('.')
    for (const key of arr) {
        obj = property_of(obj, key)
        if (!obj) {
            break
        }
    }
    return obj
}

export function parse_query_params_for_filters(
    route_params: string | undefined | null | Record<string, unknown>,
): Record<string, string> {
    let qs: Record<string, string>
    if (route_params != null && typeof route_params === 'object' && !Array.isArray(route_params)) {
        qs = {}
        // Mithril's query parser yields booleans for `true`/`false` and an array for `key[]`, which reads
        // as the comma list a multi-value filter travels as. A nested `key[a]` object has no filter meaning.
        for (const [k, v] of Object.entries(route_params)) {
            if (typeof v === 'string') {
                qs[k] = v
            } else if (Array.isArray(v)) {
                qs[k] = v.join(',')
            } else if (typeof v === 'number' || typeof v === 'boolean') {
                qs[k] = String(v)
            }
        }
    } else {
        const segment = String(route_params ?? '').replace(/^\?/, '')
        qs = segment.trim() ? Object.fromEntries(new URLSearchParams(segment)) : {}
    }
    return fold_indexed_params(qs)
}

/**
 * A multi-value filter travels as a comma list (`category=5,51`); Mithril's buildQueryString
 * writes an indexed form (`category[0]=5&category[1]=51`) for any array that reaches it. Fold
 * the indexed form back, because URLs written before the filters serialized as comma lists are
 * still bookmarked, shared and indexed.
 */
function fold_indexed_params(qs: Record<string, string>): Record<string, string> {
    const indexed = new Map<string, [number, string][]>()
    for (const [key, value] of Object.entries(qs)) {
        const [, name, position] = /^([^[\]]+)\[(\d+)]$/.exec(key) ?? []
        if (name !== undefined && position !== undefined) {
            const bucket = indexed.get(name) ?? []
            bucket.push([Number(position), value])
            indexed.set(name, bucket)
        }
    }
    if (indexed.size === 0) {
        return qs
    }

    const next: Record<string, string> = {}
    for (const [key, value] of Object.entries(qs)) {
        if (!/^[^[\]]+\[\d+]$/.test(key)) {
            next[key] = value
        }
    }
    for (const [name, pairs] of indexed) {
        // A comma list in the same URL is the current form, and wins over its indexed leftovers.
        if (name in next) {
            continue
        }
        pairs.sort((a, b) => a[0] - b[0])
        next[name] = pairs.map(([, value]) => value).join(',')
    }
    return next
}

/**
 * Query params as strings, with a multi-value param as a comma list. Mithril's buildQueryString
 * writes an array as `name[0]=&name[1]=`, so every array has to be joined before it reaches the
 * router — see `fold_indexed_params` for the URLs written before that was true.
 */
export type QueryParamValue = string | number | boolean | null | undefined | readonly (string | number | boolean)[]

export function to_query_params(params: Record<string, QueryParamValue>): Record<string, string> {
    const query: Record<string, string> = {}
    for (const [key, value] of Object.entries(params)) {
        if (value === undefined || value === null) {
            continue
        }
        query[key] = Array.isArray(value) ? value.join(',') : String(value)
    }
    return query
}

export function get_route(path: string, include_params: Record<string, QueryParamValue> = {}, exclude_params: string[] = []) {
    const current_params = include_params

    if (exclude_params.length > 0) {
        for (const param_key of Object.keys(current_params)) {
            if (exclude_params.includes(param_key)) {
                delete current_params[param_key]
            }
        }
    }

    if (Object.keys(current_params).length > 0) {
        return `${path}?${new URLSearchParams(to_query_params(current_params)).toString()}`
    }
    return path
}

export function group_by<T>(values: T[], key_of: (value: T) => PropertyKey): Record<PropertyKey, T[]> {
    const groups: Record<PropertyKey, T[]> = {}
    for (const value of values) {
        const key = key_of(value)
        const group = groups[key]
        if (group) {
            group.push(value)
        } else {
            groups[key] = [value]
        }
    }
    return groups
}

/**
 * Identifies keys present in the reference object but missing in the target object,
 * concatenating them into a dot notation path and adding to the diff array.
 * @param reference - The reference object to compare keys from.
 * @param target - The target object to compare keys against.
 * @param diff - An array to store the paths of keys that are in reference
 * but not in target.
 * @param currentPath - The current path being traversed, for nested objects.
 */
export function key_diff(reference: object, target: object, diff: string[], currentPath?: string[]) {
    if (!currentPath) {
        currentPath = []
    }
    for (const key of Object.keys(reference)) {
        // Check if the key exists in target; if not, it's a difference
        if (!(key in target)) {
            diff.push([...currentPath, key].join('.'))
            continue
        }
        const reference_value = property_of(reference, key)
        const target_value = property_of(target, key)
        if (
            typeof reference_value === 'object' &&
            reference_value !== null &&
            typeof target_value === 'object' &&
            target_value !== null
        ) {
            // Both are objects (and not null), recurse into them
            currentPath.push(key)
            key_diff(reference_value, target_value, diff, currentPath)
            currentPath.pop()
        } else if (typeof reference_value !== typeof target_value) {
            // Types don't match (e.g., reference has string but target has object)
            diff.push([...currentPath, key].join('.'))
        }
    }
}

export function key_path(obj: unknown, path: unknown) {
    if (typeof path !== 'string') {
        return null
    }
    const _path = path.split('.')
    let _obj: unknown = obj
    while (_path.length > 0) {
        const key = _path.shift()
        if (key === undefined) {
            break
        }
        if (_obj === null || _obj === undefined) {
            return undefined
        }
        _obj = property_of(_obj, key)
    }

    return _obj
}

/** Every member of the union `U` at once: the shape of an object that has been merged with each of them. */
type Intersection<U> = (U extends unknown ? (member: U) => void : never) extends (member: infer I) => void ? I : never

/**
 * Deeply merges multiple source objects into a target object. It recursively
 * merges only own and enumerable properties of the source objects into the
 * target object. Arrays and primitive types are overwritten by assignment.
 * @param target - The target object to merge properties into.
 * @param sources - One or more source objects from which to copy properties.
 * @returns The target object after merging.
 */
// One source keeps its own type, which a generic source has to: Intersection<S> can't be resolved for it.
export function merge_deep<T, S>(target: T, source: S): T & S
export function merge_deep<T, S extends unknown[]>(target: T, ...sources: S): T & Intersection<S[number]>
export function merge_deep(target: Record<string, unknown>, ...sources: Record<string, unknown>[]): unknown {
    if (sources.length === 0) {
        return target
    }
    const source = sources.shift()

    if (is_object(target) && is_object(source)) {
        for (const key in source) {
            if (Array.isArray(source[key]) && Array.isArray(target[key])) {
                // Splice the contents of source[key] into target[key]
                target[key].splice(0, target[key].length, ...(source[key] as unknown[]))
            } else if (is_object(source[key])) {
                if (!target[key]) {
                    Object.assign(target, {[key]: {}})
                }
                merge_deep(target[key], source[key])
            } else {
                Object.assign(target, {[key]: source[key]})
            }
        }
    }

    return merge_deep(target, ...sources)
}

export function object_to_query_string(obj: object) {
    return Object.entries(obj)
        .map(([key, value]) => {
            if (typeof value === 'object' && value !== null) {
                // Convert nested objects to a stringified JSON format
                return `${encodeURIComponent(key)}=${encodeURIComponent(JSON.stringify(value))}`
            }
            // Convert other values to a URL-encoded format
            return `${encodeURIComponent(key)}=${encodeURIComponent(String(value))}`
        })
        .join('&')
}

export enum PlausibleCartSource {
    CART = 'cart',
    CART_FIX = 'cart_fix',
    RECOMMENDATIONS = 'recommendations',
    OFFERS = 'offers',
    SPOTLIGHT = 'spotlight',
}

export function plausible_event(event: PlausibleEvent | string, data: Record<string, unknown>) {
    if (globalThis.__SSR_MODE__) {
        return
    }
    if (!is_one_of(Object.values(PlausibleEvent), event)) {
        return
    }
    window.plausible?.(event, data)
}

export function product_photo_image_location(
    host: string,
    product_photo: {s3_location: string; s3_location_thumbnail?: string | null},
): string {
    const image_location = (product_photo.s3_location_thumbnail || product_photo.s3_location)
        .replace('product_photos/', '')
        .split('/')
        .map((segment) => encodeURIComponent(segment))
        .join('/')

    return `${host}/${image_location}`
}

/**
 * Generate random string of length.
 * @param length Length of string to generate.
 * @returns
 */
export function random_string(length: number, include_numbers: boolean = true): string {
    let result = ''
    let characters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz'
    if (include_numbers) {
        characters += '0123456789'
    }
    const characters_length = characters.length
    for (let i = 0; i < length; i++) {
        result += characters.charAt(Math.floor(Math.random() * characters_length))
    }
    return result
}

/**
 * Memoization utility that caches expensive computations using a two-level cache.
 * Uses WeakMap for the primary key (automatic cleanup) and Map for the secondary key.
 *
 * @param key - Primary cache key (object, will be garbage collected automatically)
 * @param sub_key - Secondary cache key (string/number, scoped to the primary key)
 * @param factory - Function that creates the value to cache
 * @returns The cached value
 */
const once_cache = new WeakMap<object, Map<unknown, unknown>>()

export function once<T>(key: object, sub_key: unknown, factory: () => T): T {
    if (!once_cache.has(key)) {
        once_cache.set(key, new Map())
    }
    const sub_cache = present(once_cache.get(key), 'once cache entry')

    if (!sub_cache.has(sub_key)) {
        const value = factory()
        sub_cache.set(sub_key, value)
        return value
    }
    // A caller passes the same factory for a key, so the entry is what it would make.
    return unchecked<T>(sub_cache.get(sub_key))
}

// Removes the value from a mutable array.
// Assumes that the value won't occur more than once.
export function remove_from_array<T>(array: T[], value: T): T[] {
    const idx = array.indexOf(value)
    if (idx !== -1) {
        array.splice(idx, 1)
    }
    return array
}

/**
 * Deeply copies properties from source to target, and resets properties in target
 * that don't exist in source (but skips specified keys).
 * @param target - The target object to modify
 * @param source - The source object to copy from (optional)
 * @param skip_keys - Keys to skip when resetting (optional)
 *
 * An absent target is left alone only when its source is absent too, like a context's entity and its
 * bootstrapped copy looked up by the same entity type.
 */
export function reset_deep<T extends object | undefined>(target: T, source?: T | null, skip_keys?: string[]): void
export function reset_deep(
    target: Record<string, unknown>,
    source: Record<string, unknown> | null = null,
    skip_keys: string[] = [],
) {
    // If no source is provided, just reset all values in target
    if (source === null) {
        for (const key in target) {
            if (typeof target[key] === 'object' && target[key] !== null) {
                reset_deep(target[key], null, skip_keys)
            } else {
                if (skip_keys.includes(key)) {
                    continue
                }
                if (typeof target[key] === 'string') {
                    target[key] = ''
                } else if (typeof target[key] === 'number') {
                    target[key] = undefined
                } else if (typeof target[key] === 'boolean') {
                    target[key] = false
                }
            }
        }
        return
    }

    // First, copy all properties from source to target (deep merge)
    for (const key in source) {
        const source_value = source[key]
        const target_value = target[key]
        if (is_nested_object(source_value) && is_nested_object(target_value)) {
            // Recursively merge nested objects
            reset_deep(target_value, source_value, skip_keys)
        } else {
            // Copy value from source to target
            target[key] = source[key]
        }
    }

    // Then, reset properties that exist in target but not in source
    for (const key in target) {
        if (!(key in source)) {
            if (skip_keys.includes(key)) {
                continue
            }
            if (typeof target[key] === 'object' && target[key] !== null) {
                reset_deep(target[key], null, skip_keys)
            } else {
                if (typeof target[key] === 'string') {
                    target[key] = ''
                } else if (typeof target[key] === 'number') {
                    target[key] = undefined
                } else if (typeof target[key] === 'boolean') {
                    target[key] = false
                }
            }
        }
    }
}

/**
 * Converts a string into a URL-friendly slug.
 * The function takes a string, converts it to lowercase, then replaces spaces with dashes.
 * @param name - The string to be converted into a slug.
 * @returns The resulting slug.
 */
export function slugify(name: string | undefined | null): string {
    if (!name) return ''
    return Array.from(name.toLowerCase().normalize('NFD'))
        .filter((c) => {
            const code = c.charCodeAt(0)
            return code < 0x0300 || code > 0x036f
        })
        .join('')
        .replace(/[^a-z0-9\s]/g, ' ')
        .replace(/\s+/g, '_')
        .replace(/^_|_$/g, '')
}

/**
 * Stable ASCII slug for CMS / portal placeholder keys (diacritics stripped, non-alphanumeric → underscore).
 * Example: `MOËT HENNESSY` → `moet_hennessy`. Use for keys such as `brand_holders.${slug}.body` (see placeholder parent/leaf rules); empty slug often falls back to `'brand'`.
 */
export function cms_key_slug(display_name: string): string {
    return display_name
        .trim()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '_')
        .replace(/^_+|_+$/g, '')
}

/** Fisher–Yates shuffle; returns a new array (input is not mutated). */
export function shuffle_array<T>(arr: T[]): T[] {
    const pool = [...arr]
    const out: T[] = []
    for (let remaining = pool.length; remaining > 0; remaining--) {
        out.push(...pool.splice(Math.floor(Math.random() * remaining), 1))
    }
    return out
}

/**
 * Splits an incoterm string into its components.
 * If the incoterm string contains a single part, it assumes the incoterm is 'EXW'
 * and the location is the single part. If the incoterm string contains multiple parts, t
 * he first part is considered the incoterm and the rest is joined as the location.
 *
 * @param incoterm - The incoterm string to be split.
 * @returns An object containing the incoterm and location.
 */
export function split_incoterm(incoterm: string | null | undefined) {
    if (!incoterm) {
        return
    }
    const parts = incoterm.split(' - ')
    // If we split more with our -, join the rest on -
    if (parts.length === 1) {
        return {
            incoterm: 'EXW',
            location: parts[0],
        }
    }
    return {
        incoterm: parts[0],
        location: parts.slice(1).join('-'),
    }
}

export function stringify_json(data: unknown) {
    return JSON.stringify(
        data,
        function (_k: string, v: unknown) {
            if (Array.isArray(v)) {
                return JSON.stringify(v)
            }
            return v
        },
        2,
    )
        .replaceAll('\\', '')
        .replaceAll('"[', '[')
        .replaceAll(']"', ']')
        .replaceAll('"{', '{')
        .replaceAll('}"', '}')
}

export function strip_empty_values_nested_obj<T extends object>(obj: T, skip_keys?: string[]): T
export function strip_empty_values_nested_obj(obj: Record<string, unknown>, skip_keys: string[] = []) {
    Object.keys(obj).forEach((key) => {
        if (skip_keys.includes(key)) {
            return
        }
        if (obj[key] === '' || obj[key] === undefined || obj[key] === null) {
            delete obj[key]
        } else if (typeof obj[key] === 'object') {
            strip_empty_values_nested_obj(obj[key], skip_keys)
        }
    })
    return obj
}

/**
 * Calculates the sum of an array of numbers.
 *
 * @param number_array: An array with numbers to calculate the sum of.
 * @return: The calculated sum.
 */
export function sum(number_array: number[]): number {
    return number_array.reduce((a, b) => a + b, 0)
}

/**
 * Constructs a template string from the provided inputs.
 * @param strings The template strings array.
 * @param keys The keys to replace within the template.
 * @returns Returns a function that accepts replacements for the template and returns the resulting string.
 */
export function template(strings: TemplateStringsArray, ...keys: (number | string)[]) {
    return (...values: unknown[]) => {
        const last = values.at(-1)
        const dict = is_nested_object(last) ? last : {}
        const result: unknown[] = [strings[0]]
        keys.forEach((key, i) => {
            const value = typeof key === 'number' && Number.isInteger(key) ? values[key] : dict[key]
            result.push(value, strings[i + 1])
        })
        return result.join('')
    }
}

/**
 * Returns a function that will only apply a the provided function call once
 * every threshold milliseconds at most
 */
export const throttle = function throttle<A extends unknown[]>(
    threshold: number,
    func: (this: unknown, ...args: A) => void,
): (this: unknown, ...args: A) => void {
    type Timeout = ReturnType<typeof setTimeout>
    interface Context {
        last: number | null
        timeout: Timeout | undefined
    }
    const ctx: Context = {last: null, timeout: undefined}
    return function callback(this: unknown, ...args: A) {
        const now = Date.now()
        if (ctx.last && now < ctx.last + threshold) {
            if (ctx.timeout) {
                clearTimeout(ctx.timeout)
            }
            ctx.timeout = setTimeout(
                () => {
                    ctx.last = now
                    func.apply(this, args)
                },
                threshold - (now - ctx.last),
            )
        } else {
            ctx.last = now
            func.apply(this, args)
        }
    }
}

/** `text` up to the first `separator`, or all of it when there is none — what `split(separator)[0]` gives, typed as the string it always is. */
export function text_before(text: string, separator: string): string {
    const index = text.indexOf(separator)
    return index === -1 ? text : text.slice(0, index)
}

export function titleize(str: string) {
    return str.replaceAll(/\b[a-z]/g, (letter) => letter.toUpperCase())
}

export function trigger_download(url: string) {
    const iframe = document.createElement('iframe')
    iframe.style.display = 'none'
    iframe.src = url
    document.body.append(iframe)
    window.setTimeout(() => {
        iframe.parentNode?.removeChild(iframe)
    }, 60000)
}

/**
 * Creates a string that can be used for unique dynamic id attributes
 * @param length The length of the string
 * @param include_numbers Whether to include numbers in the string
 */
export function unique_id(length: number = 9, include_numbers: boolean = true) {
    let result = ''
    let characters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz'
    if (include_numbers) {
        characters += '0123456789'
    }
    const characters_length = characters.length
    for (let i = 0; i < length; i++) {
        result += characters.charAt(Math.floor(Math.random() * characters_length))
    }
    return result
}

/**
 * Converts the first character of a string to uppercase.
 * If the input string is empty or null, returns an empty string.
 * @param str The string to convert.
 * @returns The string with the first character in uppercase.
 */
export function ucfirst(str: string | null | undefined) {
    if (!str || str.length === 0) {
        return ''
    }
    return str.charAt(0).toUpperCase() + str.slice(1)
}

/**
 * Returns the current URL search parameters as a query string
 */
export function url_query_string(extra_params: Record<string, string> = {}) {
    if (!m.route.get()) {
        return ''
    }
    const url = new URL(m.route.get(), 'http://placeholder')
    const raw_query = url.search.slice(1)

    // Keep original raw encodings for existing params
    const raw_values = new Map<string, string>()
    if (raw_query) {
        raw_query.split('&').forEach((pair) => {
            if (!pair) {
                return
            }
            const [k, ...rest] = pair.split('=')
            if (!k) {
                return
            }
            raw_values.set(decodeURIComponent(k), rest.join('='))
        })
    }

    const params = new URLSearchParams(raw_query)
    Object.entries(extra_params).forEach(([k, v]) => params.set(k, v))

    if ([...params.keys()].length === 0) {
        return ''
    }

    // Rebuild query string: preserve raw encoding when not overridden
    const parts: string[] = []
    params.forEach((v, k) => {
        const raw = raw_values.get(k)
        if (raw !== undefined && !(k in extra_params)) {
            parts.push(`${encodeURIComponent(k)}=${raw}`)
        } else {
            parts.push(`${encodeURIComponent(k)}=${encodeURIComponent(v)}`)
        }
    })

    return `?${parts.join('&')}`
}

/**
 * Remove params from the query string and sets the route.
 */
export function remove_params_from_query_string(...to_remove: string[]) {
    const {path, params} = m.parsePathname(m.route.get())
    to_remove.forEach((r) => delete params[r])
    if (Object.keys(params).length > 0) {
        const params_as_string = new URLSearchParams(route_params_text(params)).toString()
        void m.route.set(`${path}?${params_as_string}`)
    } else {
        void m.route.set(path)
    }
}

/**
 * Compares two URLSearchParams objects to determine if they have different parameters.
 * @param searchParams1 The first URLSearchParams object for comparison.
 * @param searchParams2 The second URLSearchParams object for comparison.
 * @returns Returns true if there is a difference in the parameters, otherwise false.
 */
export function url_search_params_diff(searchParams1: URLSearchParams, searchParams2: URLSearchParams) {
    const params1 = Object.fromEntries(searchParams1)
    const params2 = Object.fromEntries(searchParams2)
    const params2_diff = Object.entries(params2).some(([k, v]) => v !== params1[k])
    const params1_diff = Object.entries(params1).some(([k, v]) => v !== params2[k])
    return params2_diff || params1_diff
}

export function validate_bottle_gtin(gtin: string) {
    if (gtin.match(/^[0-9]+$/) === null) {
        return 'Please enter a valid bottle GTIN'
    }

    if (![12, 13, 14].includes(gtin.length)) {
        return 'Bottle GTIN contains either 12, 13 or 14 digits'
    }

    if (!validate_check_digit(gtin)) {
        return 'GTIN check digit is incorrect'
    }
    return undefined
}

export function validate_check_digit(gtin: string) {
    const gtin_min_check = Array.from(gtin.slice(0, gtin.length - 1))
        .map(Number)
        .toReversed()

    let check_sum = 0
    let i = 1
    for (const digit of gtin_min_check) {
        if (i % 2 === 0) {
            check_sum += digit
        } else {
            check_sum += digit * 3
        }
        i++
    }

    const check_digit = (10 - (check_sum % 10)) % 10
    const same_digit_bool = check_digit.toString() === gtin.at(-1)
    return same_digit_bool
}

/**
 * Deeply compares two values for equality. Handles objects, arrays, and primitives.
 * @param a - First value to compare.
 * @param b - Second value to compare.
 * @returns True if deeply equal, false otherwise.
 */
export function is_deep_equal(a: unknown, b: unknown): boolean {
    if (a === b) {
        return true
    }
    if (typeof a !== typeof b) {
        return false
    }
    if (a && b && typeof a === 'object') {
        if (Array.isArray(a)) {
            if (!Array.isArray(b) || a.length !== b.length) {
                return false
            }
            for (let i = 0; i < a.length; i++) {
                if (!is_deep_equal(a[i], b[i])) {
                    return false
                }
            }
            return true
        }
        // Both are objects (not arrays)
        const a_keys = Object.keys(a)
        const b_keys = Object.keys(b)
        if (a_keys.length !== b_keys.length) {
            return false
        }
        for (const key of a_keys) {
            if (!Object.hasOwn(b, key) || !is_deep_equal(property_of(a, key), property_of(b, key))) {
                return false
            }
        }
        return true
    }
    // One is null/undefined or not an object
    return false
}
